import React, { useEffect, useMemo, useState } from "react"
import { ColumnDef } from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import { FormikHelpers, useFormik } from "formik"
import * as Yup from "yup"
import DataTableTemplate from "Components/templates/data-table-template"
import Modal from "Components/molecule/modal"
import AreYouSureModal from "Components/organisms/are-you-sure-modal"
import ActionButton from "Components/atoms/buttons/action-button"
import IconButton from "Components/atoms/buttons/icon-button"
import PrimaryButton from "Components/atoms/buttons/primary-button"
import TextInput from "Components/molecule/inputs/text-input"
import MultiSelect from "Components/molecule/inputs/multi-select"
import TableRowControlsContainer from "Components/atoms/containers/table-row-controls-container"
import { PlusIcon, PencilIcon, TrashIcon } from "Components/atoms/icons"
import { RegionBranchOption, ReportingRegion } from "models/region-mapping"
import {
    AdminRegionMappingSliceState, addRegion, removeRegion, setEditingRegion, setRegion, setRegions,
} from "slices/admin/region-mapping/reducer"
import {
    deleteRegionMappingRequest, getRegionMappingFormFields, getRegionMappings, submitCreateRegionMapping, submitEditRegionMapping,
} from "slices/admin/region-mapping/thunk"
import { formatDateTimeAmPm } from "utils/tools"

const selectRegions = createSelector(
    (state: any) => state.AdminRegionMapping,
    ({ regions, editingRegion }: AdminRegionMappingSliceState) => ({ regions, editingRegion })
)

interface Fields {
    code: string
    name: string
    branchIds: string[]
}

const EMPTY_FORM: Fields = { code: '', name: '', branchIds: [] }

const Validation = Yup.object({
    code: Yup.string().trim().required("Code is required").max(20, "20 characters max")
        .matches(/^[A-Za-z0-9_-]+$/, "Letters, numbers, - and _ only"),
    name: Yup.string().trim().required("Name is required").max(100, "100 characters max"),
    branchIds: Yup.array().of(Yup.string()),
})

const branchLabel = (b: { code: string, name: string }) => b.name && b.name !== b.code ? `${b.code} - ${b.name}` : b.code

const RegionMappingPage = () => {
    document.title = "Region Mapping | IbexIQ";
    const dispatch: any = useDispatch()
    const { regions, editingRegion } = useSelector(selectRegions)

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [branches, setBranches] = useState<RegionBranchOption[]>([])
    const [showFormModal, setShowFormModal] = useState<boolean>(false)
    const [focusedRegion, setFocusedRegion] = useState<ReportingRegion | null>(null)
    const [isDeleting, setIsDeleting] = useState<boolean>(false)

    const loadBranches = async () => {
        const res = await getRegionMappingFormFields()
        if (res) setBranches(res)
    }

    useEffect(() => {
        const init = async () => {
            const [res] = await Promise.all([getRegionMappings(), loadBranches()])
            if (res) dispatch(setRegions(res))
            setIsLoading(false)
        }
        init()
    }, [])

    const handleCloseFormModal = () => {
        setShowFormModal(false)
        dispatch(setEditingRegion(null))
        resetForm()
    }

    const onSubmit = async (vals: Fields, helpers: FormikHelpers<Fields>) => {
        helpers.setSubmitting(true)
        const payload = { code: vals.code.trim().toUpperCase(), name: vals.name.trim(), branchIds: vals.branchIds }

        const res = editingRegion
            ? await submitEditRegionMapping(editingRegion.id, payload)
            : await submitCreateRegionMapping(payload)

        if (res) {
            dispatch(editingRegion ? setRegion(res) : addRegion(res))
            await loadBranches()
            handleCloseFormModal()
        }
        helpers.setSubmitting(false)
    }

    const { values, errors, touched, handleBlur, handleChange, handleSubmit, setValues, setFieldValue, resetForm, isSubmitting, isValid } =
        useFormik<Fields>({ initialValues: EMPTY_FORM, onSubmit, validationSchema: Validation })

    // Branches that are free or already in the region being edited
    const branchOptions = useMemo(() => branches
        .filter(b => !b.regionId || b.regionId === editingRegion?.id)
        .map(b => ({ value: b.id, label: branchLabel(b) })), [branches, editingRegion])

    const handleAdd = () => {
        dispatch(setEditingRegion(null))
        setValues(EMPTY_FORM, false)
        setShowFormModal(true)
    }

    const handleEdit = (region: ReportingRegion) => {
        dispatch(setEditingRegion(region))
        setValues({ code: region.code, name: region.name, branchIds: region.branches.map(b => b.id) }, false)
        setShowFormModal(true)
    }

    const handleCloseDeleteModal = async () => setFocusedRegion(null)

    const confirmDelete = async () => {
        if (!focusedRegion) return
        setIsDeleting(true)
        if (await deleteRegionMappingRequest(focusedRegion.id)) {
            dispatch(removeRegion(focusedRegion.id))
            await loadBranches()
            setFocusedRegion(null)
        }
        setIsDeleting(false)
    }

    const columns = React.useMemo<ColumnDef<ReportingRegion>[]>(() => [
        { header: "Code", accessorKey: "code", size: 100, cell: ({ row }) => <strong>{row.original.code}</strong> },
        { header: "Name", accessorKey: "name", size: 200 },
        {
            header: "Branches",
            id: "branches",
            size: 380,
            accessorFn: (r) => r.branches.map(branchLabel).join(", "),
            cell: ({ row }) => row.original.branches.length
                ? row.original.branches.map(branchLabel).join(", ")
                : <span className="text-muted">No branches</span>,
        },
        {
            header: "Updated",
            id: "updated",
            size: 260,
            accessorFn: (r) => r.updatedAt ? new Date(r.updatedAt).getTime() : undefined,
            cell: ({ row }) => <TableRowControlsContainer style={{ justifyContent: 'space-between' }}>
                <span>
                    {row.original.updatedBy ?? "System (migration)"}
                    <br /><small className="text-muted">{formatDateTimeAmPm(row.original.updatedAt)}</small>
                </span>
                <div className='d-flex flex-row justify-content-end'>
                    <IconButton onClick={() => handleEdit(row.original)}><PencilIcon /></IconButton>
                    <IconButton onClick={() => setFocusedRegion(row.original)}><TrashIcon /></IconButton>
                </div>
            </TableRowControlsContainer>
        },
    ], [])

    const table = useDataTable({
        columns,
        data: regions,
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    })

    return <DataTableTemplate
        title="Admin"
        breadcrumbItem="Region Mapping"
        table={table}
        columns={columns}
        isLoading={isLoading}
        actions={[
            <ActionButton key="add" onClick={handleAdd}>Add <PlusIcon /></ActionButton>
        ]}
        modal={<>
            <Modal
                isOpen={showFormModal}
                onClose={handleCloseFormModal}
                title={editingRegion ? `Edit Region ${editingRegion.code}` : "Create Region"}
            >
                <div className="generate-documents-form">
                    <TextInput
                        name="code"
                        label="Code*"
                        placeholder="e.g. BC"
                        touched={!!touched.code}
                        value={values.code}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={!!touched.code && !!errors.code}
                        message={touched.code ? errors.code : undefined}
                    />
                    <TextInput
                        name="name"
                        label="Name*"
                        placeholder="e.g. British Columbia"
                        touched={!!touched.name}
                        value={values.name}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={!!touched.name && !!errors.name}
                        message={touched.name ? errors.name : undefined}
                    />
                    <MultiSelect
                        name="branchIds"
                        label="Branches"
                        placeholder="Select branches"
                        options={branchOptions}
                        value={branchOptions.filter(o => values.branchIds.includes(o.value))}
                        onChange={(opts) => setFieldValue("branchIds", opts.map(o => o.value))}
                        menuPortal
                    />
                    <small className="text-muted d-block mb-3">A branch can belong to one region only; branches already in another region are not listed.</small>
                    <PrimaryButton
                        type='submit'
                        onClick={() => handleSubmit()}
                        disabled={isSubmitting || !isValid}
                        className="w-100"
                    >
                        {isSubmitting ? "Saving..." : editingRegion ? "Update" : "Create"}
                    </PrimaryButton>
                </div>
            </Modal>
            <AreYouSureModal
                isOpen={!!focusedRegion}
                onModalClose={handleCloseDeleteModal}
                modalTitle="Delete Region"
                subTitle={`Delete region ${focusedRegion?.code ?? ""}? Its branches will no longer be grouped in reports.`}
                handleAreYouSureAccept={confirmDelete}
                handleAreYouSureDecline={handleCloseDeleteModal}
                areYouSureacceptText={isDeleting ? 'Deleting...' : 'Delete'}
                areYouSuredeclineText="Cancel"
            />
        </>}
    />
}

export default RegionMappingPage
